import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";

import {
    ColegioUsuarioListItem, ConflictoCredencialUnion, ContadoresUnionUsuariosColegio, CredencialUnionInfo, UsuarioColegioResumen,
    ResolucionConflictoUnion, UnionUsuariosPlan, UnionUsuariosResultado
} from "../models/dataModels";
import { RouteHelper } from "../IntranetRouter";
import { I18n } from "../utils/I18n";
import { logAndExtractInfoFromException } from "../utils/ValuesFormatter";
import { ColegioUsuariosService } from "./ColegioUsuariosService";
import { ColegioUsuariosPicker } from "./Picker";

export interface UnirUsuariosModalProps {
    /** Se invoca al cerrar el modal; `unidos` indica si se ha aplicado una unión (para refrescar el listado). */
    onClose: (unidos: boolean) => void;
}

type Fase = 'seleccion' | 'confirmacion' | 'uniendo' | 'resultado';

interface UsuarioElegido {
    id: number;
    nombre: string;
}

const textos = () => I18n.Strings.unirUsuarios;

const nombreCompleto = (u: UsuarioColegioResumen) => `${u?.nombre ?? ''} ${u?.apellidos ?? ''}`.trim() || '—';

const fechaHora = (valor: Date | string | null | undefined, siNo: string) =>
    valor == null ? siNo : (I18n.Strings.formatDateTime(new Date(valor)) || siNo);

/** Clave de un conflicto para guardar su resolución. */
const claveConflicto = (c: ConflictoCredencialUnion) => `${c.licenciaEliminarId}-${c.licenciaDestinoId}`;

/** Enlace que abre la ficha del usuario en una pestaña nueva, para comprobar quién es quién antes de unir. */
const enlaceDetalle = (usuarioId: number | null | undefined) => usuarioId == null ? null :
    <a href={RouteHelper.editUsuarioColegio(usuarioId)} target="_blank" rel="noopener noreferrer"
        className="ms-1" title={textos().verDetalle}>
        <i className="bi bi-box-arrow-up-right" />
    </a>;

/** Filas de la tabla de contadores por curso. */
const filasContadores = (): { texto: string, valor: (c: ContadoresUnionUsuariosColegio) => number }[] => [
    { texto: textos().contMatriculasATraspasar, valor: c => c?.matriculasATraspasar ?? 0 },
    { texto: textos().contMatriculasDuplicadas, valor: c => c?.matriculasDuplicadas ?? 0 },
    { texto: textos().contLicenciasConCredenciales, valor: c => c?.licenciasConCredencialesATraspasar ?? 0 },
    { texto: textos().contLicenciasSinCredenciales, valor: c => c?.licenciasSinCredencialesATraspasar ?? 0 },
    { texto: textos().contLicenciasDescartadas, valor: c => c?.licenciasADescartar ?? 0 },
    { texto: textos().contConflictos, valor: c => c?.conflictos ?? 0 }
];

/** Opciones de resolución de un conflicto, en el orden en que se muestran. */
const opcionesResolucion = (): { valor: ResolucionConflictoUnion, texto: string }[] => [
    { valor: ResolucionConflictoUnion.ConservarDestino, texto: textos().opcionConservarDestino },
    { valor: ResolucionConflictoUnion.ConservarEliminado, texto: textos().opcionConservarEliminado },
    { valor: ResolucionConflictoUnion.Ninguna, texto: textos().opcionNinguna }
];

/**
 * Unión de dos usuarios del colegio. Se eligen el usuario a eliminar y el que se mantiene, se previsualiza el
 * plan (lo que se traspasa en el curso actual y en los anteriores, avisos y bloqueos), se resuelven los
 * conflictos de credenciales (uno a uno en el curso actual, con una única opción para los anteriores) y, tras
 * confirmar, se ejecuta. Plan y unión los calcula Admin, que tiene el único motor de unión: es el mismo flujo
 * que su modal "Unir usuarios del colegio", acotado al colegio de la sesión.
 */
export function UnirUsuariosModal({ onClose }: UnirUsuariosModalProps) {
    const [fase, setFase] = useState<Fase>('seleccion');
    const [origen, setOrigen] = useState<UsuarioElegido | null>(null);
    const [destino, setDestino] = useState<UsuarioElegido | null>(null);
    const [plan, setPlan] = useState<UnionUsuariosPlan | null>(null);
    const [planificando, setPlanificando] = useState(false);
    const [resultado, setResultado] = useState<UnionUsuariosResultado | null>(null);
    const [anadirAlias, setAnadirAlias] = useState(true);
    const [resoluciones, setResoluciones] = useState<Record<string, ResolucionConflictoUnion>>({});
    const [resolucionHistorico, setResolucionHistorico] = useState<ResolucionConflictoUnion | null>(null);
    const [verHistorico, setVerHistorico] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Descarta previsualizaciones obsoletas si cambia la selección mientras carga.
    const seq = useRef(0);
    // Se incrementa para volver a pedir el plan con los mismos usuarios (tras un rechazo al unir).
    const [recalculos, setRecalculos] = useState(0);
    // Error del intento de unión que provocó el recálculo, para seguir mostrándolo con el plan nuevo.
    const errorAlRecalcular = useRef<string | null>(null);
    // Se ha llamado a unir alguna vez: si falló la comunicación la unión puede haberse aplicado, así que al
    // cerrar hay que refrescar el listado aunque no se haya llegado a ver el resultado.
    const intentoUnir = useRef(false);

    // Previsualización en cuanto hay dos usuarios distintos elegidos.
    useEffect(() => {
        // El motivo del rechazo anterior se conserva aunque el plan nuevo también falle: si la unión se
        // aplicó pese al error de comunicación, el recálculo dirá que el usuario ya no existe, y sin el
        // aviso previo ese mensaje no se entiende.
        const previo = errorAlRecalcular.current;
        const conPrevio = (mensaje: string) => previo ? `${previo} ${mensaje}` : mensaje;

        setPlan(null);
        setError(previo);
        errorAlRecalcular.current = null;

        if (!origen || !destino || origen.id === destino.id) {
            // Invalida la previsualización en vuelo: si no, su respuesta pintaría un plan que ya no corresponde.
            seq.current++;
            setPlanificando(false);
            return;
        }

        const mio = ++seq.current;
        setPlanificando(true);

        new ColegioUsuariosService().planificarUnion(origen.id, destino.id)
            .then(resp => {
                if (mio !== seq.current) {
                    return;
                }

                if (!resp.isOk) {
                    setError(conPrevio(resp.errorMsg || textos().errorPlan));
                    return;
                }

                // Cada conflicto del curso actual arranca con la resolución recomendada; los de cursos
                // anteriores no tienen valor por defecto: hay que elegir.
                const recomendadas: Record<string, ResolucionConflictoUnion> = {};
                (resp.conflictosCursoActual ?? []).forEach(c => recomendadas[claveConflicto(c)] = c.recomendada);

                setResoluciones(recomendadas);
                setResolucionHistorico(null);
                setVerHistorico(false);
                setPlan(resp);
            })
            .catch(ex => {
                if (mio === seq.current) {
                    setError(conPrevio(`${textos().errorPlan} ${logAndExtractInfoFromException(ex)}`));
                }
            })
            .then(() => {
                if (mio === seq.current) {
                    setPlanificando(false);
                }
            });
        // Por id a propósito: volver a elegir el mismo usuario no debe repetir la previsualización.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [origen?.id, destino?.id, recalculos]);

    /** Cambia el papel de los dos usuarios elegidos: el que se borra pasa a mantenerse y al revés. */
    const intercambiar = () => {
        setOrigen(destino);
        setDestino(origen);
    };

    const conflictosActuales = plan?.conflictosCursoActual ?? [];
    const conflictosAnteriores = plan?.conflictosCursosAnteriores ?? [];
    const mismoUsuario = origen != null && destino != null && origen.id === destino.id;
    const conflictosResueltos = conflictosActuales.every(c => resoluciones[claveConflicto(c)] != null)
        && (conflictosAnteriores.length === 0 || resolucionHistorico != null);
    const puedeUnir = origen != null && destino != null && !mismoUsuario &&
        plan != null && plan.esPosible === true && !planificando && conflictosResueltos;

    /** Vuelve a calcular el plan tras un rechazo, conservando el motivo para mostrarlo junto al plan nuevo. */
    const recalcularTrasRechazo = (motivo: string) => {
        errorAlRecalcular.current = `${motivo} ${textos().recalculado}`;
        setFase('seleccion');
        setRecalculos(n => n + 1);
    };

    const unir = () => {
        if (!origen || !destino || !plan) {
            return;
        }

        setFase('uniendo');
        setError(null);
        intentoUnir.current = true;

        new ColegioUsuariosService().unir({
            colegioId: 0,
            usuario: null,
            usuarioOrigenId: origen.id,
            usuarioDestinoId: destino.id,
            confirmado: true,
            huellaPlan: plan.huellaPlan,
            anadirAliasEmailOrigen: anadirAlias,
            resoluciones: conflictosActuales.map(c => ({
                licenciaEliminarId: c.licenciaEliminarId,
                licenciaDestinoId: c.licenciaDestinoId,
                resolucion: resoluciones[claveConflicto(c)]
            })),
            resolucionCursosAnteriores: conflictosAnteriores.length > 0 ? resolucionHistorico : null
        })
            .then(resp => {
                // El rechazo más habitual es que los datos cambiaron desde la previsualización (la huella
                // incluye el uso de las credenciales): se vuelve a calcular el plan para revisarlo y reintentar.
                if (!resp.isOk) {
                    recalcularTrasRechazo(resp.errorMsg || textos().errorUnir);
                    return;
                }

                setResultado(resp);
                setFase('resultado');
            })
            .catch(ex => recalcularTrasRechazo(`${textos().errorUnir} ${logAndExtractInfoFromException(ex)}`));
    };

    const filaUsuario = (u: UsuarioColegioResumen, esSuperviviente: boolean) =>
        <tr key={u.id} className={esSuperviviente ? 'table-success' : 'table-warning'}>
            <td className="text-center">
                {esSuperviviente
                    ? <i className="bi bi-star-fill text-success" title={textos().seMantiene} />
                    : <i className="bi bi-trash text-danger" title={textos().seBorra} />}
            </td>
            <td>
                {u.login}{enlaceDetalle(u.id)}
                <div className="small text-muted">{nombreCompleto(u)} · Id {u.id}</div>
                {(u.clasesCursoActual ?? []).length > 0 && <div className="small text-muted">
                    {textos().cursoActualClases((u.clasesCursoActual ?? []).join(', '))}
                </div>}
            </td>
            <td className="text-end">{u.numMatriculas ?? 0}</td>
            <td className="text-end">{u.numLicenciasConCredenciales ?? 0} / {u.numLicencias ?? 0}</td>
            <td className="text-end">{u.numEstudiantes ?? 0}</td>
            <td className="text-end">{u.numAliasEmail ?? 0}</td>
            <td>{u.borrado
                ? <span className="text-danger">{textos().borrado}</span>
                : <span className="text-muted">{textos().activo}</span>}</td>
        </tr>;

    /** Detalle de una credencial de un conflicto: código, fechas de uso y qué pasaría si se descarta. */
    const detalleCredencial = (credencial: CredencialUnionInfo) => <>
        <code>{credencial?.codigo}</code>
        <div className="small text-muted">
            {textos().asignada}: {fechaHora(credencial?.fechaAsignacion, '—')}
            {' · '}{textos().ultimoAcceso}: {fechaHora(credencial?.ultimoAcceso, textos().nunca)}
            {' · '}{textos().notificada}: {fechaHora(credencial?.notificada, '—')}
        </div>
        <div className="small text-muted">
            {credencial?.delAlmacen
                ? (credencial.volveriaAlAlmacen ? textos().delAlmacenVuelve : textos().delAlmacenNoVuelve)
                : textos().credencialManual}
        </div>
    </>;

    const opcionConflicto = (conflicto: ConflictoCredencialUnion, opcion: ResolucionConflictoUnion, texto: string,
        credencial?: CredencialUnionInfo) => {
        const clave = claveConflicto(conflicto);
        const id = `unir-conflicto-${clave}-${opcion}`;

        return <div className="form-check">
            <input className="form-check-input" type="radio" id={id} name={`unir-conflicto-${clave}`}
                checked={resoluciones[clave] === opcion}
                disabled={fase !== 'seleccion'}
                onChange={() => setResoluciones({ ...resoluciones, [clave]: opcion })} />
            <label className="form-check-label small" htmlFor={id}>
                {texto}
                {conflicto.recomendada === opcion && <span className="badge bg-success ms-1">{textos().recomendada}</span>}
                {credencial && <div>{detalleCredencial(credencial)}</div>}
            </label>
        </div>;
    };

    const conflictoActual = (conflicto: ConflictoCredencialUnion) => {
        const opciones = opcionesResolucion();

        return <div key={claveConflicto(conflicto)} className="border rounded p-2 mb-2">
            <div className="mb-1">
                <b>{conflicto.titulo}</b>
                <span className="small text-muted ms-2">{conflicto.isbn} · {conflicto.clase} ({conflicto.cursoEscolar})</span>
            </div>
            <Row>
                <Col md={5}>
                    {opcionConflicto(conflicto, opciones[0].valor, opciones[0].texto, conflicto.credencialDestino)}
                </Col>
                <Col md={5}>
                    {opcionConflicto(conflicto, opciones[1].valor, opciones[1].texto, conflicto.credencialEliminar)}
                </Col>
                <Col md={2}>
                    {opcionConflicto(conflicto, opciones[2].valor, opciones[2].texto)}
                </Col>
            </Row>
        </div>;
    };

    const bloqueHistorico = () => <div className="border rounded p-2 mt-3">
        <h6 className="mb-1">{textos().conflictosAnterioresTitulo(conflictosAnteriores.length)}</h6>
        <p className="small mb-2">{textos().conflictosAnterioresAyuda}</p>
        {opcionesResolucion().map(o => <div key={o.valor} className="form-check form-check-inline">
            <input className="form-check-input" type="radio" id={`unir-historico-${o.valor}`} name="unir-historico"
                checked={resolucionHistorico === o.valor}
                disabled={fase !== 'seleccion'}
                onChange={() => setResolucionHistorico(o.valor)} />
            <label className="form-check-label small" htmlFor={`unir-historico-${o.valor}`}>{o.texto}</label>
        </div>)}
        {resolucionHistorico == null && <div className="small text-danger">{textos().faltaOpcion}</div>}
        <div>
            <Button type="button" color="link" size="sm" className="p-0" onClick={() => setVerHistorico(!verHistorico)}>
                {verHistorico ? textos().ocultarDetalle : textos().mostrarDetalle}
            </Button>
        </div>
        {verHistorico && <table className="table table-sm mt-2 mb-0">
            <thead className="table-light">
                <tr>
                    <th>{textos().colLibro}</th>
                    <th>{textos().colClase}</th>
                    <th>{textos().colDelQueSeMantiene}</th>
                    <th>{textos().colDelQueSeElimina}</th>
                </tr>
            </thead>
            <tbody>
                {conflictosAnteriores.map(c => <tr key={claveConflicto(c)}>
                    <td>{c.titulo} <span className="small text-muted">{c.isbn}</span></td>
                    <td>{c.clase} ({c.cursoEscolar})</td>
                    <td><code>{c.credencialDestino?.codigo}</code></td>
                    <td><code>{c.credencialEliminar?.codigo}</code></td>
                </tr>)}
            </tbody>
        </table>}
    </div>;

    const selectorUsuario = (label: JSX.Element, elegido: UsuarioElegido | null,
        onSelected: (u: UsuarioElegido | null) => void) =>
        <ColegioUsuariosPicker
            label={label}
            required={true}
            disabled={fase !== 'seleccion'}
            itemId={elegido?.id ?? null}
            itemName={elegido?.nombre ?? ''}
            size="xl"
            getIdFromItem={(item: ColegioUsuarioListItem) => item.id}
            getNameFromItem={(item: ColegioUsuarioListItem) => `${item.nombre ?? ''} ${item.apellidos ?? ''} (${item.login})`.trim()}
            onSelected={(id, nombre) => onSelected(id == null ? null : { id, nombre })}
        />;

    const cerrar = () => onClose(fase === 'resultado' || intentoUnir.current);

    return <Modal isOpen={true} size="xl" toggle={fase === 'uniendo' ? undefined : cerrar}>
        {/* Con la unión en marcha no se cierra: no se vería el resultado ni se refrescaría el listado. */}
        <ModalHeader close={<button className="btn-close" disabled={fase === 'uniendo'} onClick={cerrar} />}>
            {textos().titulo}
        </ModalHeader>
        <ModalBody>
            {fase !== 'resultado' && <>
                <Row className="g-2 align-items-end">
                    <Col>
                        {selectorUsuario(<><i className="bi bi-trash text-danger me-1" />{textos().usuarioEliminar}{enlaceDetalle(origen?.id)}</>,
                            origen, setOrigen)}
                    </Col>
                    <Col xs="auto" className="mb-3">
                        <Button type="button" outline color="secondary" size="sm" title={textos().intercambiar}
                            disabled={fase !== 'seleccion' || (origen == null && destino == null)}
                            onClick={intercambiar}>
                            <i className="bi bi-arrow-left-right" />
                        </Button>
                    </Col>
                    <Col>
                        {selectorUsuario(<><i className="bi bi-star-fill text-success me-1" />{textos().usuarioMantener}{enlaceDetalle(destino?.id)}</>,
                            destino, setDestino)}
                    </Col>
                </Row>

                {mismoUsuario && <div className="alert alert-warning mt-3 mb-0">{textos().mismoUsuario}</div>}

                {planificando && <div className="text-center text-muted py-3">
                    <span className="spinner-border spinner-border-sm me-1" role="status" /> {textos().calculandoPlan}
                </div>}

                {plan && <>
                    <table className="table table-sm table-hover align-middle mt-3">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '40px' }} className="text-center">✓</th>
                                <th>{textos().colUsuario}</th>
                                <th className="text-end">{textos().colMatriculas}</th>
                                <th className="text-end" title={textos().colLicenciasTitulo}>{textos().colLicencias}</th>
                                <th className="text-end">{textos().colEstudiantes}</th>
                                <th className="text-end">{textos().colAlias}</th>
                                <th>{textos().colEstado}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {plan.destino && filaUsuario(plan.destino, true)}
                            {plan.origen && filaUsuario(plan.origen, false)}
                        </tbody>
                    </table>

                    <table className="table table-sm small mb-0">
                        <thead className="table-light">
                            <tr>
                                <th></th>
                                <th className="text-end">{textos().cursoActual}</th>
                                <th className="text-end">{textos().cursosAnteriores}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filasContadores().map(f => <tr key={f.texto}>
                                <td>{f.texto}</td>
                                <td className="text-end">{f.valor(plan.cursoActual)}</td>
                                <td className="text-end">{f.valor(plan.cursosAnteriores)}</td>
                            </tr>)}
                        </tbody>
                    </table>

                    {plan.esPosible === false && <div className="alert alert-danger mt-3 mb-0">
                        <p className="mb-2">{textos().noSePuedeUnir}</p>
                        <ul className="mb-0">
                            {(plan.bloqueos ?? []).map((b, i) => <li key={i}>{b}</li>)}
                        </ul>
                    </div>}

                    {plan.esPosible !== false && conflictosActuales.length > 0 && <div className="mt-3">
                        <h6 className="mb-1">{textos().conflictosActualesTitulo}</h6>
                        <p className="small mb-2">{textos().conflictosActualesAyuda}</p>
                        {conflictosActuales.map(conflictoActual)}
                    </div>}

                    {plan.esPosible !== false && conflictosAnteriores.length > 0 && bloqueHistorico()}

                    {(plan.avisos ?? []).length > 0 && <div className="alert alert-warning mt-3 mb-0">
                        <ul className="mb-0 small">
                            {(plan.avisos ?? []).map((a, i) => <li key={i}>{a}</li>)}
                        </ul>
                    </div>}

                    {plan.aliasEmailAAnadir && <div className="form-check mt-3">
                        <input className="form-check-input" type="checkbox" id="unir-alias"
                            checked={anadirAlias}
                            disabled={fase !== 'seleccion'}
                            onChange={e => setAnadirAlias(e.target.checked)} />
                        <label className="form-check-label small" htmlFor="unir-alias">
                            {textos().conservarAlias(plan.aliasEmailAAnadir)}
                        </label>
                    </div>}
                </>}

                {fase === 'confirmacion' && <div className="alert alert-danger mt-3 mb-0">
                    <i className="bi bi-exclamation-triangle me-1" />
                    {textos().confirmacion(plan?.origen?.login ?? '', plan?.destino?.login ?? '')}
                </div>}

                {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
            </>}

            {fase === 'resultado' && resultado && <div className="alert alert-success mb-0">
                <p className="mb-2">
                    <i className="bi bi-check-lg me-1" />
                    {textos().resultado(resultado.loginOrigen ?? '', resultado.loginDestino ?? '')}
                </p>
                {(resultado.log ?? []).length > 0 && <ul className="mb-0">
                    {(resultado.log ?? []).map((l, i) => <li key={i}>{l}</li>)}
                </ul>}
            </div>}
        </ModalBody>
        <ModalFooter>
            {fase === 'seleccion' && <>
                <Button color="secondary" onClick={cerrar}>{I18n.Strings.cancel}</Button>
                <Button color="danger" disabled={!puedeUnir} onClick={() => { setError(null); setFase('confirmacion'); }}>
                    {textos().unir}
                </Button>
            </>}

            {fase === 'confirmacion' && <>
                <Button color="secondary" onClick={() => setFase('seleccion')}>{textos().volver}</Button>
                <Button color="danger" disabled={!puedeUnir} onClick={unir}>{textos().unirDefinitivamente}</Button>
            </>}

            {fase === 'uniendo' && <Button color="danger" disabled>
                <span className="spinner-border spinner-border-sm me-1" role="status" /> {textos().uniendo}
            </Button>}

            {fase === 'resultado' && <Button color="primary" onClick={cerrar}>{textos().cerrar}</Button>}
        </ModalFooter>
    </Modal>;
}
